import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "container",
    "titleInput",
    "personIdInput",
    "contentInput",
    "errorMessage",
    "errorText",
    "submitButton",
    "submitText",
    "spinner"
  ]
  
  static values = {
    searchUrl: String,
    createUrl: String,
    triggerId: String
  }
  
  connect() {
    // Bind to external trigger button if specified
    if (this.hasTriggerIdValue && this.triggerIdValue) {
      const trigger = document.getElementById(this.triggerIdValue)
      if (trigger) {
        this.boundOpen = this.open.bind(this)
        trigger.addEventListener("click", this.boundOpen)
      }
    }
  }
  
  disconnect() {
    // Remove event listener from trigger
    if (this.boundOpen && this.hasTriggerIdValue) {
      const trigger = document.getElementById(this.triggerIdValue)
      if (trigger) {
        trigger.removeEventListener("click", this.boundOpen)
      }
    }
  }
  
  open() {
    this.element.classList.remove("hidden")
    document.body.classList.add("overflow-hidden")
    // Focus on title input
    setTimeout(() => {
      if (this.hasTitleInputTarget) {
        this.titleInputTarget.focus()
      }
    }, 100)
  }
  
  close() {
    this.element.classList.add("hidden")
    document.body.classList.remove("overflow-hidden")
    this.resetForm()
  }
  
  resetForm() {
    if (this.hasTitleInputTarget) this.titleInputTarget.value = ""
    if (this.hasContentInputTarget) this.contentInputTarget.value = ""
    if (this.hasPersonIdInputTarget) this.personIdInputTarget.value = ""
    
    // Clear the person search input
    const personSearchInput = document.getElementById("note-person-search")
    if (personSearchInput) personSearchInput.value = ""
    
    this.hideError()
  }
  
  showError(message) {
    if (this.hasErrorMessageTarget && this.hasErrorTextTarget) {
      this.errorTextTarget.textContent = message
      this.errorMessageTarget.classList.remove("hidden")
    }
  }
  
  hideError() {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.add("hidden")
    }
  }
  
  async submit(event) {
    event.preventDefault()
    
    const content = this.hasContentInputTarget ? this.contentInputTarget.value.trim() : ""
    
    if (!content) {
      this.showError("Please enter some notes")
      return
    }
    
    // Show loading state
    this.setLoading(true)
    this.hideError()
    
    // Build form data
    const formData = {
      meeting: {
        title: this.hasTitleInputTarget ? this.titleInputTarget.value.trim() : "",
        raw_transcript: content,
        source: "quick_note",
        person_id: this.hasPersonIdInputTarget ? this.personIdInputTarget.value : null,
        auto_approve: true  // Auto-approve quick notes
      }
    }
    
    try {
      const response = await fetch(this.createUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: JSON.stringify(formData)
      })
      
      const data = await response.json()
      
      if (data.success || data.id) {
        // Redirect to the meeting page or show success
        if (data.url) {
          window.location.href = data.url
        } else if (data.id) {
          window.location.href = `/meetings/${data.id}`
        } else {
          // Just reload dashboard
          window.location.reload()
        }
      } else {
        this.showError(data.errors ? data.errors.join(", ") : "Failed to save note")
        this.setLoading(false)
      }
    } catch (error) {
      console.error("Error saving meeting note:", error)
      this.showError("An error occurred. Please try again.")
      this.setLoading(false)
    }
  }
  
  setLoading(loading) {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = loading
    }
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.toggle("hidden", !loading)
    }
    if (this.hasSubmitTextTarget) {
      this.submitTextTarget.textContent = loading ? "Processing..." : "Save & Process"
    }
  }
}

